import { Box, HStack, IndicatorDot, IndicatorDotVariants, JITTooltip, Text } from '@talos/kyoko';
import type { ColDef, ICellRendererParams, IRowNode, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { DefaultMarketAccount } from '../../DefaultMarketAccount/DefaultMarketAccount';
import { useMarketSelectorContext } from '../MarketSelectorContext';
import { AvailabilityEnum } from '../types';
import { isMarketSelectorItemSelectable } from '../utils/items';
import type { MarketSelectorRow } from './types';

type MarketColumnValue = string;

export const marketSelectorAutoGroupColumnDef = {
  headerName: 'Markets',
  sortable: true,
  comparator: marketsColumnComparator,
  sort: 'asc',
  valueGetter: (params: ValueGetterParams<MarketSelectorRow>): MarketColumnValue | undefined => {
    // FYI this valueGetter is only ran for leaf rows. AgGrid automatically takes the grouping key for group rows.
    return params.node?.data?.item.name;
  },
  valueFormatter: ({ value, context }: ValueFormatterParams<MarketSelectorRow, MarketColumnValue>) => {
    if (!value) {
      return '';
    }

    return (
      context.current.marketDisplayNameByName?.get(value) ??
      context.current.marketAccountsByName?.get(value)?.DisplayName ??
      value
    );
  },
  cellRendererParams: (params: ICellRendererParams) => ({
    innerRenderer: MarketSelectorMarketCellRenderer,
    suppressCount: true,
    ...params,
  }),
} satisfies ColDef;

const MarketSelectorMarketCellRenderer = ({
  value,
  context,
  node,
  data,
}: ICellRendererParams<MarketSelectorRow, MarketColumnValue>) => {
  const { itemsMap, itemsByMarket } = useMarketSelectorContext();
  if (!value) {
    return 'Unknown';
  }

  const isGroup = node.group;
  if (isGroup) {
    const selectable = itemsByMarket.get(value)?.some(item => isMarketSelectorItemSelectable(item));
    const label = context.current.marketDisplayNameByName?.get(value) ?? value;

    return (
      <HStack color={selectable ? 'colorTextImportant' : 'colorTextSubtle'} gap="spacing4">
        <Text>{label}</Text>
        {!selectable && (
          <JITTooltip tooltip="No Accounts selectable within Market" usePortal>
            <Box pl="spacing4">
              <IndicatorDot variant={IndicatorDotVariants.Warning} show size={8} />
            </Box>
          </JITTooltip>
        )}
      </HStack>
    );
  }

  const item = itemsMap.get(value);
  if (!item) {
    return 'Unknown';
  }

  const availabilityContent =
    item.availability.availability < AvailabilityEnum.Ok ? item.availability.render?.() : undefined;

  const isOnlyChild = (node?.parent?.childrenAfterGroup?.length ?? 1) === 1;

  return (
    <HStack color={isMarketSelectorItemSelectable(item) ? 'colorTextImportant' : 'colorTextSubtle'} gap="spacing4">
      <Text>{item.displayName}</Text>
      {availabilityContent && (
        <JITTooltip tooltip={availabilityContent} usePortal>
          <Box pl="spacing4">
            <IndicatorDot variant={IndicatorDotVariants.Warning} show size={8} />
          </Box>
        </JITTooltip>
      )}
      {!isGroup && !isOnlyChild && <DefaultMarketAccount marketAccount={item.name} />}
    </HStack>
  );
};

function marketsColumnComparator(
  _valueA: unknown,
  _valueB: unknown,
  nodeA: IRowNode<MarketSelectorRow>,
  nodeB: IRowNode<MarketSelectorRow>
) {
  const labelA = nodeA.data?.item.displayName;
  const labelB = nodeB.data?.item.displayName;
  if (!labelA || !labelB) {
    return 0;
  }

  return labelA.localeCompare(labelB);
}

/**
 * When our node is a market parent row, as in we are a market row with account children within us,
 * we are selectable if at least one of our children is selectable.
 */
export function isMarketParentRowSelectable(node: IRowNode<MarketSelectorRow>): boolean {
  const children = node.allLeafChildren ?? [];
  return children.some(child => (child.data?.item ? isMarketSelectorItemSelectable(child.data.item) : false));
}
